import React, { useRef, useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { FiPlus, FiX, FiInfo } from 'react-icons/fi';
import { Sketch } from '@uiw/react-color';
import { useLanguage } from '../../Context/LanguageContext';
import CustomTooltip from '../../Components/CustomTooltip';
import { OutlinedPrimaryButton, PrimaryButton, CancelButton } from '../../Components/Buttons';
import AIBadge from '../../Components/AIBadge';

export const ColorPickerField = ({ label, name, value, onChange, placeholder, description }) => {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPicker]);

  const handleColorChange = (color) => {
    onChange({ target: { name, value: color.hex } });
  };

  return (
    <Form.Group className="mb-4">
      {label && (
        <>
          <Form.Label className="onboarding-form-label" style={{ fontSize: '0.8rem', marginBottom: '0.35rem' }}>
            <span>{label}</span>
          </Form.Label>
          {description && (
            <p className="form-section-description" style={{ marginBottom: '0.5rem' }}>{description}</p>
          )}
        </>
      )}
      <div className="onboarding-color-picker-wrapper" ref={wrapperRef} style={{ position: 'relative' }}>
        <div className="onboarding-color-input-group">
          <div
            className="onboarding-color-swatch"
            style={{ backgroundColor: value || '#e5e7eb' }}
            onClick={() => setShowPicker(!showPicker)}
          />
          <input
            type="text"
            className="onboarding-color-input"
            value={value || ''}
            onChange={(e) => {
              const val = e.target.value.trim();
              onChange({ target: { name, value: val && !val.startsWith('#') ? '#' + val : val } });
            }}
            placeholder={placeholder || '#55297f'}
          />
          {value && (
            <button
              type="button"
              className="btn btn-sm p-0 ms-1"
              onClick={() => onChange({ target: { name, value: '' } })}
              title="Clear"
              style={{ color: 'var(--text-muted)', opacity: 0.6 }}
            >
              <FiX size={14} />
            </button>
          )}
        </div>
        {showPicker && (
          <div
            className="color-picker-popover"
            ref={pickerRef}
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '0',
              marginBottom: '8px',
              zIndex: 1000,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <Sketch color={value || '#55297f'} onChange={handleColorChange} />
          </div>
        )}
      </div>
    </Form.Group>
  );
};

export const MultiSelectField = ({ label, name, value = [], options, onChange, max = 5, allowOther = true, description, icon, aiConfidence = null }) => {
  const { t } = useLanguage();
  const [otherValue, setOtherValue] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);

  // Normalize options to support both strings and objects
  const normalizedOptions = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  const handleCheckboxChange = (optionValue, checked) => {
    let newValue = [...value];
    if (checked) {
      if (newValue.length >= max) return;
      newValue.push(optionValue);
    } else {
      newValue = newValue.filter(v => v !== optionValue);
    }
    onChange({ target: { name, value: newValue } });
  };

  const handleAddOther = () => {
    if (otherValue && !value.includes(otherValue)) {
      if (value.length >= max) return;
      onChange({ target: { name, value: [...value, otherValue] } });
      setOtherValue('');
      setShowOtherInput(false);
    }
  };

  return (
    <Form.Group className="mb-4">
      {label && (
        <>
          <Form.Label className="onboarding-form-label">
            {icon && <span style={{ color: 'var(--primary)' }}>{icon}</span>}
            <span>{label}</span>
            <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.8em' }}>
              ({value.length}/{max})
            </span>
            {aiConfidence !== null && <AIBadge confidence={aiConfidence} className="ai-badge-inline" />}
          </Form.Label>
          {description && (
            <p className="form-section-description">{description}</p>
          )}
        </>
      )}
      <div className="onboarding-checkbox-card-group">
        {normalizedOptions.map((option) => (
          <Form.Check
            key={option.value}
            type="checkbox"
            id={`${name}-${option.value}`}
            className="onboarding-checkbox-card"
          >
            <Form.Check.Input
              type="checkbox"
              checked={value.includes(option.value)}
              onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
              disabled={!value.includes(option.value) && value.length >= max}
            />
            <Form.Check.Label>
              {option.description && (
                <span className="onboarding-card-info-icon">
                  <CustomTooltip content={option.description} placement="top" showOnMobile={true}>
                    <FiInfo />
                  </CustomTooltip>
                </span>
              )}
              {option.icon && <span className="onboarding-card-option-icon">{option.icon}</span>}
              <span className="onboarding-card-label-text">{option.label}</span>
            </Form.Check.Label>
          </Form.Check>
        ))}
      </div>

      {/* Custom Values Tags */}
      {value.filter(v => !normalizedOptions.some(opt => opt.value === v)).length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {value.filter(v => !normalizedOptions.some(opt => opt.value === v)).map((customVal) => (
            <span
              key={customVal}
              className="badge d-flex align-items-center gap-1"
              style={{
                background: 'rgba(85, 41, 127, 0.08)',
                color: 'var(--primary-dark)',
                border: '1px solid rgba(85, 41, 127, 0.2)',
                padding: '0.4rem 0.6rem',
                fontWeight: 500,
                fontSize: '0.8rem'
              }}
            >
              {customVal}
              <FiX
                size={14}
                style={{ cursor: 'pointer', marginLeft: '4px' }}
                onClick={() => handleCheckboxChange(customVal, false)}
              />
            </span>
          ))}
        </div>
      )}

      {allowOther && (
        <div className="mt-3">
          {!showOtherInput ? (
            <OutlinedPrimaryButton
              size="sm"
              onClick={() => setShowOtherInput(true)}
              disabled={value.length >= max}
              padding="0.4rem 0.8rem"
              fontSize="13px"
            >
              <FiPlus size={14} className="me-1" /> {t('common.addOther') || 'Add other'}
            </OutlinedPrimaryButton>
          ) : (
            <div className="d-flex gap-2 align-items-center">
              <Form.Control
                type="text"
                value={otherValue}
                onChange={(e) => setOtherValue(e.target.value)}
                placeholder={t('common.enterCustomOption') || "Enter custom option"}
                size="sm"
                className="onboarding-form-input"
                style={{ maxWidth: '200px' }}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOther())}
                autoFocus
              />
              <PrimaryButton size="sm" onClick={handleAddOther} padding="0.4rem 0.8rem" fontSize="13px">
                {t('common.add') || 'Add'}
              </PrimaryButton>
              <CancelButton size="sm" onClick={() => { setShowOtherInput(false); setOtherValue(''); }} padding="0.4rem 0.8rem" fontSize="13px">
                {t('common.cancel') || 'Cancel'}
              </CancelButton>
            </div>
          )}
        </div>
      )}
    </Form.Group>
  );
};

export const SingleChoiceField = ({ label, name, value, options, onChange, descriptions = {}, description, icon, aiConfidence = null }) => {
  // Normalize options
  const normalizedOptions = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt, description: descriptions[opt] } : opt
  );

  return (
    <Form.Group className="mb-4">
      {label && (
        <>
          <Form.Label className="onboarding-form-label">
            {icon && <span style={{ color: 'var(--primary)' }}>{icon}</span>}
            <span>{label}</span>
            {aiConfidence !== null && <AIBadge confidence={aiConfidence} className="ai-badge-inline" />}
          </Form.Label>
          {description && (
            <p className="form-section-description">{description}</p>
          )}
        </>
      )}
      <div className="onboarding-radio-card-group">
        {normalizedOptions.map((option) => (
          <Form.Check
            key={option.value}
            type="radio"
            id={`${name}-${option.value}`}
            className="onboarding-radio-card"
          >
            <Form.Check.Input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange({ target: { name, value: e.target.value } })}
            />
            <Form.Check.Label>
              {option.description && (
                <span className="onboarding-card-info-icon">
                  <CustomTooltip content={option.description} placement="top" showOnMobile={true}>
                    <FiInfo />
                  </CustomTooltip>
                </span>
              )}
              {option.icon && <span className="onboarding-card-option-icon">{option.icon}</span>}
              <span className="onboarding-card-label-text">{option.label}</span>
            </Form.Check.Label>
          </Form.Check>
        ))}
      </div>
    </Form.Group>
  );
};
